import type * as THREE from 'three';
import { fray, ringMail, sewn, strips, type ArmorKit } from './human-armors.js';

/**
 * What bandits wear, one look per stat block (`content/encounters/bandits.ts`). They are low-level enemies and there are many of them
 * on screen, so each is a handful of fitted layers over the body's own tunic and hose in the crew's cloth, built from the shared
 * materials: no gilding, no jewels, a few seams. The grade shows in the kit, from the minion's rope belt up to the captain's mail.
 * Weapons are drawn plain through `ArmorVisual.rough`; hoods, caps and masks come from the unit's head parts. Each look puts the gang's mark (`k.emblem`) on its chest; the cloak carries it too.
 */
export const banditLooks: Record<string, (k: ArmorKit) => void> = {};
const parts = (k: ArmorKit) => ({ ...k.at, ...k.m });

/** A belt: a band round the waist with a plain buckle. */
function belt(k: ArmorKit, y: number, grow: number, material: THREE.Material, buckle: THREE.Material, width = .06) {
  k.torsoLayer(y - width / 2, y + width / 2, grow, material);
  k.plate(buckle, .026, .008, k.onTorso(0, y, grow));
}
/** A pouch hung from the belt on the right hip. */
function pouch(k: ArmorKit, material: THREE.Material, y: number, side = 1) {
  k.ellipsoid(k.hips, material, side * (k.at.pw + .034), y - k.at.h - .02, .01, .036, .05, .034);
}

/** A minion: a ragged tunic, a rope belt, one torn sleeve and rags bound round the shins. */
banditLooks['bandit-rags'] = k => {
  const { h, waist, s, elbow, armTop, knee, ankle, cloth, leather, wood } = parts(k);
  k.gown(h - .17, .035, .012, cloth, h - .17, waist, fray(h - .17, .045, 8));
  k.stack(waist - .045, waist + .012, .02, k.torsoLayer, wood, .02, .005);
  k.armPiece(-1, elbow - .02, armTop, .01, cloth, fray(elbow - .02, .04, 7));
  k.stack(ankle + .06, knee - .05, .012, k.legLayer, leather, .052, .004);
  sewn(k, { on: 'torso', face: -1, at: [.07, s - .1], w: .07, h: .06, roll: .2, seed: 11, grow: .011, material: leather, thread: k.m.ivory });
  k.emblem({ x: 0, y: s - .14, size: .35, grow: .018 });
  pouch(k, wood, waist);
};

/** A bandit: a leather vest crossed by a strap, a belt and pouch, bracers, boots to the knee and one scavenged steel pauldron. */
banditLooks['bandit-leathers'] = k => {
  const { h, waist, s, shoulder, armTop, elbow, wrist, knee, neckBase, ankle, leather, steel, trim, dark } = parts(k);
  k.torsoLayer(waist - .06, s + .005, .016, leather);
  for (const face of [1, -1]) k.ribbon(k.run([-.12, s], [.1, h + .13], .028, face, 0, 14), .04, .008, dark);
  belt(k, h + .06, .022, leather, steel);
  pouch(k, leather, h + .02);
  k.armLayer(wrist + .02, elbow - .05, .011, leather); k.armLayer(elbow - .08, elbow - .066, .015, dark);
  k.legLayer(ankle, knee - .08, .011, leather); k.legLayer(knee - .12, knee - .075, .017, leather);
  k.armPiece(1, shoulder - .1, armTop, .02, steel); k.armPiece(1, shoulder - .104, shoulder - .094, .024, leather);
  k.neckLoft(neckBase - .002, neckBase + .024, .012, trim);
  k.emblem({ x: 0, y: s - .14, size: .35, grow: .042 });
};

/** A hunter: a laced jerkin over a short skirt, a cloth mantle under the hood, bracers, a belt and boots turned down at the knee. */
banditLooks['bandit-hunter'] = k => {
  const { h, waist, s, shoulder, armTop, elbow, wrist, knee, neckBase, ankle, cloth, leather, steel, ivory, dark } = parts(k);
  k.torsoLayer(waist - .02, s + .03, .012, leather); k.skirt(h - .085, .03, .013, leather);
  for (const y of [s - .02, s - .075]) k.ribbon(k.run([-.03, y], [.03, y - .02], .02, 1, 0, 4), .008, .004, ivory);
  k.torsoLayer(s - .05, neckBase, .024, cloth); k.armLayer(shoulder - .06, armTop, .012, cloth);
  k.armLayer(wrist + .02, elbow - .05, .009, leather); k.armLayer(elbow - .065, elbow - .045, .013, dark);
  belt(k, h + .055, .02, dark, steel, .05); pouch(k, leather, h + .02, -1);
  k.emblem({ x: 0, y: s - .14, size: .35, grow: .03 });
  k.legLayer(ankle, knee - .1, .011, leather); k.legLayer(knee - .13, knee - .09, .019, leather);
};

/** An assassin: dark wraps crossed over the chest, a tail of cloth at the hip, bound forearms, soft boots and a row of throwing knives. */
banditLooks['bandit-shadow'] = k => {
  const { h, waist, s, shoulder, armTop, elbow, wrist, knee, neckBase, ankle, cloth, clothDark, leather, steel, dark } = parts(k);
  k.torsoLayer(waist - .05, s + .01, .01, clothDark);
  for (const face of [1, -1]) for (const flip of [-1, 1]) k.ribbon(k.run([flip * .12, s], [-flip * .1, waist - .03], .02, face, 0, 12), .036, .007, leather);
  belt(k, waist, .02, dark, steel, .045);
  const tail = k.loincloth({ top: h + .06, hem: h - .36, width: .06, dip: .045, flare: .02, ragged: .006 });
  tail(h - .36, h + .06, .02, cloth);
  k.armLayer(wrist + .01, elbow - .02, .01, leather); for (const y of [wrist + .05, wrist + .12]) k.armLayer(y, y + .012, .014, dark);
  k.armLayer(shoulder - .07, armTop, .011, clothDark);
  k.legLayer(ankle, knee, .01, leather); k.legLayer(knee - .06, knee - .045, .014, dark);
  for (let i = 0; i < 4; i++) { const spot = k.onTorso(-.07 + i * .028, s - .05 - Math.abs(i - 1.5) * .008, .03); k.box(k.torso, steel, .01, .052, .006, spot.point.x, spot.point.y - k.at.torsoY, spot.point.z); }
  k.neckLoft(neckBase - .01, neckBase + .03, .016, cloth);
  k.emblem({ x: 0, y: s - .14, size: .35, grow: .042 });
};

/** A bruiser: bands of leather round the chest under a riveted steel plate, mismatched pauldrons, a wide belt on a skirt of straps and wrapped fists. */
banditLooks['bandit-brute'] = k => {
  const { h, waist, s, shoulder, armTop, wrist, knee, ankle, leather, steel, dark } = parts(k);
  k.stack(waist - .05, s - .01, .02, k.torsoLayer, leather, .06, .008);
  sewn(k, { on: 'torso', at: [0, s - .08], w: .17, h: .15, roll: 0, seed: 21, grow: .03, depth: .012, material: steel, rivets: dark, rough: .07 });
  strips(k, { count: 10, top: h + .075, length: .2, flare: .1, width: .07, grow: .034, material: leather });
  belt(k, h + .06, .03, dark, steel, .065);
  k.armPiece(-1, shoulder - .13, armTop, .034, leather); k.armPiece(-1, shoulder - .135, shoulder - .12, .039, dark);
  k.armPiece(1, shoulder - .12, armTop, .031, steel); k.armPiece(1, shoulder - .124, shoulder - .114, .036, leather);
  k.armLayer(wrist + .01, wrist + .2, .016, leather); for (const y of [wrist + .06, wrist + .15]) k.armLayer(y, y + .012, .02, steel);
  k.legLayer(ankle, knee - .05, .013, leather); k.legLayer(knee - .1, knee - .04, .021, dark);
  k.emblem({ x: 0, y: s - .12, size: .38, grow: .046 }); // on the steel plate
};

/** A mage: a weathered robe, a trailing sash, bell sleeves and bone-coloured stitched edging. */
banditLooks['bandit-robe'] = k => {
  const { h, waist, s, shoulder, armTop, wrist, neckBase, ankle, cloth, clothDark, leather, ivory } = parts(k);
  const hem = ankle + .1;
  k.torsoLayer(waist - .08, neckBase - .01, .012, cloth);
  k.gown(hem, .05, .014, cloth); k.gown(hem, .05, .019, clothDark, hem, hem + .05); k.gown(hem, .05, .021, ivory, hem + .05, hem + .058);
  k.torsoLayer(waist - .04, waist + .03, .022, leather); k.torsoLayer(waist + .03, waist + .037, .024, ivory);
  const tail = k.loincloth({ top: waist, hem: hem + .1, width: .035, dip: .03, flare: .06, ragged: 0 });
  tail(hem + .1, waist, .04, leather);
  for (const side of [-1, 1]) {
    k.armPiece(side, wrist, armTop, .011, cloth);
    for (let i = 0; i < 3; i++) k.armPiece(side, wrist - .004 + i * .035, wrist + .04 + i * .035, .042 - i * .011, cloth);
    k.armPiece(side, wrist - .004, wrist + .008, .043, ivory);
  }
  k.torsoLayer(s - .035, neckBase, .03, clothDark); k.torsoLayer(shoulder - .12, shoulder - .114, .03, ivory);
  k.stud(ivory, k.onTorso(0, s - .06, .034), .014, .01);
  k.emblem({ x: 0, y: s - .14, size: .35, grow: .042 });
};

/** A captain: a mail hauberk and sleeves under a tabard in the gang's colour, steel pauldrons, bracers and greaves, a plain buckle. */
banditLooks['bandit-captain'] = k => {
  const { h, waist, s, shoulder, armTop, elbow, wrist, knee, neckBase, ankle, leather, trim, dark } = parts(k);
  const iron = k.mat('metal', .6, .48), mail = ringMail(k.mat('metal', .55, .6, 0x777c80), 22), sleeve = ringMail(k.mat('metal', .55, .6, 0x777c80), 8, 1.2);
  k.stack(waist - .1, s + .005, .015, k.torsoLayer, mail, .03, .005); k.torsoLayer(waist - .105, waist - .095, .02, iron);
  k.stack(elbow - .08, armTop, .013, k.armLayer, sleeve, .03, .004);
  k.stack(s - .02, neckBase, .034, k.torsoLayer, mail, .024, .006);
  const coat = { top: h + .07, hem: h - .2, width: .11, dip: .04, flare: .035, ragged: 0 }, panel = k.loincloth(coat);
  panel(coat.hem, coat.top, .022, trim); panel(coat.hem - .004, coat.hem + .016, .026, dark);
  belt(k, h + .06, .03, leather, iron, .05);
  for (const side of [-1, 1]) {
    for (let i = 0; i < 2; i++) { const from = shoulder - .12 + i * .045; k.armPiece(side, from, i ? armTop : from + .055, .03 + i * .006, iron); }
    k.armPiece(side, wrist + .01, wrist + .18, .014, leather); k.armPiece(side, wrist + .17, wrist + .185, .018, iron);
  }
  k.legLayer(ankle, knee - .02, .012, leather); k.legLayer(ankle + .1, knee - .04, .017, iron); k.legLayer(knee - .04, knee - .026, .02, dark);
  for (const limb of [k.leftLeg, k.rightLeg]) k.ellipsoid(limb.lower, iron, 0, .01, .064, .036, .038, .018);
  k.emblem({ x: 0, y: s - .13, size: .38, grow: .04 });
};
